import React, { useRef } from 'react';
import { LOGO_SRC } from '../BrandLogo';
import { formatUsd } from '../../data/studyPlans';
import {
  categoryLabel,
  formatPaymentDate,
  modeLabel,
  subjectNameById,
} from '../../utils/payments';
import { SBA } from '../../theme';

/**
 * Official printable / downloadable payment receipt.
 */
export default function PaymentReceipt({ payment, printId = 'payment-receipt-print' }) {
  const ref = useRef(null);
  if (!payment) return null;

  const items = payment.breakdown?.items || [{ name: payment.label, amount: payment.amount }];

  function handlePrint() {
    window.print();
  }

  return (
    <div>
      <div style={s.actions} className="no-print">
        <button type="button" onClick={handlePrint} style={s.btnPrimary}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>print</span>
          Print / Download receipt
        </button>
      </div>

      <div id={printId} ref={ref} style={s.sheet} className="payment-receipt-sheet">
        <div style={s.header}>
          <img src={LOGO_SRC} alt="Success Bridge Academy" style={s.logo} />
          <div>
            <h1 style={s.org}>Success Bridge Academy</h1>
            <p style={s.tag}>GED AI Learning Hub — Official Payment Receipt</p>
          </div>
        </div>

        <div style={s.refRow}>
          <div>
            <p style={s.metaLabel}>Reference number</p>
            <p style={s.ref}>{payment.referenceNumber || '—'}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={s.metaLabel}>Payment date</p>
            <p style={s.metaVal}>{formatPaymentDate(payment)}</p>
          </div>
        </div>

        <div style={s.grid}>
          <div>
            <p style={s.metaLabel}>Order</p>
            <p style={s.metaVal}>Hosted bank checkout</p>
          </div>
          <div>
            <p style={s.metaLabel}>Category</p>
            <p style={s.metaVal}>{categoryLabel(payment.category)}</p>
          </div>
          <div>
            <p style={s.metaLabel}>Mode of payment</p>
            <p style={s.metaVal}>{modeLabel(payment.mode)}</p>
          </div>
          <div>
            <p style={s.metaLabel}>Status</p>
            <p style={s.metaVal}>{String(payment.status || 'paid').toUpperCase()}</p>
          </div>
        </div>

        <p style={s.labelLine}>
          <strong>Description:</strong> {payment.label}
        </p>

        {payment.subjects?.length ? (
          <p style={s.labelLine}>
            <strong>Subjects:</strong> {payment.subjects.map(subjectNameById).join(', ')}
          </p>
        ) : null}

        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Item</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={`${item.name}-${i}`}>
                <td style={s.td}>{item.name}</td>
                <td style={{ ...s.td, textAlign: 'right' }}>{formatUsd(item.amount)}</td>
              </tr>
            ))}
            {Number(payment.breakdown?.serviceAndTax) > 0 ? (
              <tr>
                <td style={s.td}>Service charges &amp; taxes</td>
                <td style={{ ...s.td, textAlign: 'right' }}>
                  {formatUsd(payment.breakdown.serviceAndTax)}
                </td>
              </tr>
            ) : null}
          </tbody>
          <tfoot>
            <tr>
              <td style={s.totalLabel}>Total paid</td>
              <td style={s.totalVal}>{formatUsd(payment.amount)}</td>
            </tr>
          </tfoot>
        </table>

        <div style={s.footerMeta}>
          <div style={s.stampArea}>
            <img src={LOGO_SRC} alt="Official stamp" style={s.stamp} />
            <div style={s.stampText}>
              <p style={s.stampLabel}>Official Receipt</p>
              <p style={s.stampSub}>Success Bridge Academy</p>
            </div>
          </div>
          <p style={s.official}>
            This is an official receipt issued by Success Bridge Academy. Keep it for your records.
          </p>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #${printId}, #${printId} * { visibility: visible !important; }
          #${printId} {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            box-shadow: none !important;
            border: none !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const s = {
  actions: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 10,
    border: 'none',
    background: SBA.navy,
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  sheet: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: '28px 30px',
    boxShadow: '0 12px 36px rgba(27,54,93,0.1)',
    color: '#0f172a',
  },
  header: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 22 },
  logo: { width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' },
  org: { margin: 0, fontSize: 20, fontWeight: 800, color: SBA.navy },
  tag: { margin: '4px 0 0', fontSize: 12, color: '#64748b' },
  refRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    padding: '14px 16px',
    borderRadius: 12,
    background: 'rgba(27,54,93,0.05)',
    marginBottom: 18,
  },
  metaLabel: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#64748b',
  },
  ref: { margin: '4px 0 0', fontSize: 18, fontWeight: 800, color: SBA.navy, fontFamily: 'monospace' },
  metaVal: { margin: '4px 0 0', fontSize: 14, fontWeight: 700 },
  email: { margin: '2px 0 0', fontSize: 12, color: '#64748b' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 14,
    marginBottom: 16,
  },
  labelLine: { margin: '0 0 8px', fontSize: 13, color: '#334155' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 12, marginBottom: 18 },
  th: {
    textAlign: 'left',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#64748b',
    borderBottom: '2px solid #e2e8f0',
    padding: '8px 6px',
  },
  td: { padding: '10px 6px', borderBottom: '1px solid #f1f5f9', fontSize: 13 },
  totalLabel: {
    padding: '12px 6px',
    fontWeight: 800,
    fontSize: 14,
    color: SBA.navy,
    borderTop: '2px solid #e2e8f0',
  },
  totalVal: {
    padding: '12px 6px',
    fontWeight: 800,
    fontSize: 16,
    textAlign: 'right',
    color: SBA.navy,
    borderTop: '2px solid #e2e8f0',
  },
  footerMeta: { marginTop: 8 },
  stampArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    padding: '12px 0',
    borderBottom: '1px dashed #e2e8f0',
  },
  stamp: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px double #1b365d',
    opacity: 0.85,
  },
  stampText: { flex: 1 },
  stampLabel: { margin: 0, fontSize: 13, fontWeight: 800, color: SBA.navy, textTransform: 'uppercase', letterSpacing: '0.06em' },
  stampSub: { margin: '2px 0 0', fontSize: 11, color: '#64748b' },
  footLine: { margin: '0 0 4px', fontSize: 12, color: '#475569' },
  official: {
    margin: '14px 0 0',
    fontSize: 11,
    color: '#94a3b8',
    borderTop: '1px dashed #e2e8f0',
    paddingTop: 12,
  },
};
